import DairyService from '@src/api/services/dairy';
import { RouteTypes } from '@src/routes/constants';
import React, { useContext, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import Layout from '@src/components/Layout';
import { El } from './style';
import { Dimmer, Loader, Tab } from 'semantic-ui-react';
import { Outlet, useLocation } from 'react-router-dom';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { ThemeContext } from 'styled-components';
import CustomIcon from '~/components/CustomIcon';
import { useAuth } from '~/routes/components/AuthProvider';
import { handleErrors, onError } from '~/utils/helpers';
import { ADMIN_ROLE_SLUG, BASE_PRICE_TEXT_SLUG, CLIMATE_CHECK_SLUG, CO2_LFL_SLUG, DAIRY_USERS_PROFILE_COUNT_USER_ID, LIGHT_USER_ROLE_SLUG, MILK_MONEY_ACCOUNT_SLUG, OPERATIVE_ROLE_SLUG } from '~/Constants';

const rolesWithPermissionViewEdit = [ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG, LIGHT_USER_ROLE_SLUG]
const rolesAdminLight = [ADMIN_ROLE_SLUG, LIGHT_USER_ROLE_SLUG]
const rolesWithEditPermissions = [ADMIN_ROLE_SLUG, OPERATIVE_ROLE_SLUG]

const DairyFactoryProfile: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { systemData, systemData: { modules, operativeUsersCanSeeFarmersInfo, logged: { roleSlug }, chEnv } } = useAuth()
  const { logoTitle, firstColor } = useContext(ThemeContext)
  const [counters, setCounters] = useState({
    farmers: 0,
    users: 0,
    farmerGroups: 0
  })
  const [isBusy, setIsBusy] = useState(false)
  const [basePrice, setBasePrice] = useState(systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG))

  const operativeUserLogged = roleSlug === OPERATIVE_ROLE_SLUG
  const milkMoneyModuleEnabled = modules.find(module => module.slug === MILK_MONEY_ACCOUNT_SLUG)
  const co2ModuleEnabled = modules.find(module => module.slug === CO2_LFL_SLUG)
  const climaCheckModuleEnabled = modules.find(module => module.slug === CLIMATE_CHECK_SLUG)
  const isSwissDairy = chEnv === 1

  useEffect(() => {
    if (roleSlug !== OPERATIVE_ROLE_SLUG) {
      fetchCounters()
    } else if (operativeUserLogged && operativeUsersCanSeeFarmersInfo === 1) {
      fetchCounters()
    }
  }, [])

  useEffect(() => {
    setBasePrice(systemData?.texts.find(text => text.slug === BASE_PRICE_TEXT_SLUG))
  }, [t, systemData])

  async function fetchCounters() {
    setIsBusy(true)
    try {
      const farmersResponse = await DairyService.countAllActiveFarmers()
      const usersResponse = await DairyService.countAllActiveUsers()
      const farmerGroupsResponse = await DairyService.countAllActiveFarmerGroups()
      setCounters({
        farmers: farmersResponse.data,
        users: usersResponse.data,
        farmerGroups: farmerGroupsResponse.data
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  const panes = [
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'users-tab',
        content: t('Dairy users'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_USERS,
        key: 'users',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'farmer-groups-tab',
        content: t('Farmer Groups'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_FARMER_GROUPS,
        key: 'farmer-groups',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'locations-tab',
        content: t('Locations'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_LOCATIONS,
        key: 'locations',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'individual-filters-tab',
        content: t('Individual filters'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_INDIVIDUAL_FILTERS,
        key: 'individual-filters',
        display: isSwissDairy ? [] : rolesWithEditPermissions
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'milk-types-tab',
        content: t('Milk types'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MILK_TYPES,
        key: 'milk-types',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'milk-payment-tab',
        content: t('Export MGA'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MILK_PAYMENT,
        key: 'milk-payment',
        display: isSwissDairy ? [] : rolesWithEditPermissions
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'banks-tab',
        content: t('Banks'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_BANKS,
        key: 'banks',
        display: operativeUsersCanSeeFarmersInfo === 1 ? rolesWithPermissionViewEdit : rolesAdminLight
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'co2-export-tab',
        content: <div dangerouslySetInnerHTML={{ __html: 'CO<sub>2</sub>-Export' }} />,
        to: RouteTypes.DAIRY_FACTORY_PROFILE_CO2_EXPORT,
        key: 'co2-export',
        display: co2ModuleEnabled ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'clima-export-tab',
        content: t('Clima Export'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_CLIMA_EXPORT,
        key: 'clima-export',
        display: climaCheckModuleEnabled ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'vat-tab',
        content: t('VAT'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_VAT,
        key: 'vat',
        display: rolesWithEditPermissions
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'base-price-tab',
        content: basePrice?.translation || basePrice?.label,
        to: RouteTypes.DAIRY_FACTORY_PROFILE_BASE_PRICE,
        key: 'base-price',
        display: rolesWithPermissionViewEdit
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'milk-prices-tab',
        content: t('Current Milk Price'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MILK_PRICES,
        key: 'milk-prices',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'husbandry-tab',
        content: t('Forms of husbandry'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_HUSBANDRY,
        key: 'husbandry',
        display: rolesWithPermissionViewEdit,
        remove: isSwissDairy
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'shop-managment-tab',
        content: t('Shop managment'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_SHOP,
        key: 'shop-managment',
        display: milkMoneyModuleEnabled ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'segmentation-tab',
        content: t('Segmentation'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_SEGMENTATION,
        key: 'segmentation',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'associations-tab',
        content: t('Associations'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_ASSOCIATIONS,
        key: 'associations',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'members-tab',
        content: t('Membership Statuses'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_MEMBERSHIP_STATUSES,
        key: 'members',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'certifications-tab',
        content: t('Certifications'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_CERTIFICATIONS,
        key: 'certifications',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },

    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'collection-types-tab',
        content: t('Collection types'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_COLLECTION_TYPES,
        key: 'collection-types',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'collection-cycles-tab',
        content: t('Collection Cycles'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_COLLECTION_CYCLES,
        key: 'collection-cycles',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'contract-types-tab',
        content: t('Contract Types'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_CONTRACT_TYPES,
        key: 'contract-types',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'billing-groups-tab',
        content: t('Billing Groups'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_BILLING_GROUPS,
        key: 'billing-groups',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'transport-tariffs-tab',
        content: t('Transport Tariffs'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_TRANSPORT_TARIFFS,
        key: 'transport-tariffs',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'transportation-areas-tab',
        content: t('Transportation Area'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_TRANSPORTATION_AREAS,
        key: 'transportation-areas',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'production-zones-tab',
        content: t('Production Zones'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_PRODUCTION_ZONES,
        key: 'production-zones',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'operating-forms-tab',
        content: t('Operating Forms'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_OPERATING_FORMS,
        key: 'operating-forms',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'restriction-periods-tab',
        content: t('Restriction periods'),
        to: RouteTypes.DAIRY_FACTORY_PROFILE_RESTRICTION_PERIODS,
        key: 'restriction-periods',
        display: milkMoneyModuleEnabled && isSwissDairy ? rolesWithPermissionViewEdit : []
      }
    },
    {
      menuItem: {
        as: El.DairyTabItem,
        id: 'co2-calculation',
        content: <div dangerouslySetInnerHTML={{ __html: 'CO<sub>2</sub>-Kalkulation' }} />,
        to: RouteTypes.DAIRY_FACTORY_PROFILE_PURCHASED_AMOUNTS,
        key: 'co2-calculation',
        display: co2ModuleEnabled ? rolesWithPermissionViewEdit : []
      }
    }
  ];

  const { data: dairyProfileData, isLoading: isDairyProfileDataLoading } = useQuery(
    'profile',
    DairyService.getDairyProfile,
    {
      onError,
      retry: 1,
      refetchOnMount: true,
      refetchOnWindowFocus: false
    }
  );

  function renderCounter() {
    return < El.DairyDetailsCounter firstColor={firstColor}>
      <div style={{ position: 'relative', display: 'flex' }}>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-farmer-icon font-size-4' size='big' />
          <div className='label'>{t('Farmers')}</div>
          <div className='value'>{counters.farmers}</div>
        </div>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-user-group font-size-4' size='big' />
          <div className='label'>{t('Users')}</div>
          <div className='value' id={DAIRY_USERS_PROFILE_COUNT_USER_ID}>{counters.users}</div>
        </div>
        <div className='dairy-details-counter-column'>
          <CustomIcon className='icon-teamwork font-size-4' size='big' />
          <div className='label'>{t('Farmer groups')}</div>
          <div className='value'>{counters.farmerGroups}</div>
        </div>
        <Dimmer inverted active={isBusy}>
          <Loader active={isBusy} />
        </Dimmer>
      </div>
    </El.DairyDetailsCounter>
  }

  const renderDairyDetailsCardBody = () => (
    <El.DairyProfileCardBody>
      <El.DairyProfileLogo>
        <div className='dairy-logo-container'>
          <LazyLoadImage
            alt={''}
            effect='blur'
            src={logoTitle} />
        </div>
        <div className='dairy-name-container'>
          <div className='dairy-name'>{dairyProfileData?.companyName}</div>
        </div>
      </El.DairyProfileLogo>
      {roleSlug !== OPERATIVE_ROLE_SLUG || operativeUsersCanSeeFarmersInfo === 1 ? renderCounter() : null}
    </El.DairyProfileCardBody >
  );

  function renderDairyDetailsCardFooter() {
    const panesFiltered = panes?.filter(pane => !pane.menuItem.remove).filter(pane => pane.menuItem.display.find(item => item === roleSlug))
    const index = panesFiltered.findIndex(pane => pane.menuItem.to === location.pathname)
    return <El.DiaryProfileCardFooter>
      <Tab
        activeIndex={index}
        menu={{ secondary: true, pointing: true }}
        panes={panesFiltered}
      />
    </El.DiaryProfileCardFooter>
  }

  return (
    <Layout
      segmented
      isBusy={isDairyProfileDataLoading}
      title={t('Dairy Factory')}
    >
      <El.DairyProfileCard>
        <div>
          {renderDairyDetailsCardBody()}
        </div>
        {renderDairyDetailsCardFooter()}
      </El.DairyProfileCard>
      <Outlet />
    </Layout>
  );
};

export default DairyFactoryProfile;
